<template>
  <v-container
    class="px-0 py-0 bg-super-light-blue"
    data-testid="license-page-template"
    fluid
  >
    <template v-if="license">
      <PageTitle>
        <div class="d-flex flex-1-0 align-start px-4 py-3 w-100">
          <div class="d-flex flex-column flex-1-0">
            <v-row
              align="center"
              class="fs-14 c-light-black fw-500"
              dense
              no-gutters
            >
              <v-col class="d-flex">
                <router-link
                  :to="backRoute"
                  class="hover-underlined c-light-black fw-500"
                  data-testid="license-show-breadcrumb"
                >
                  {{ backName }}
                </router-link>

                <span class="px-3">/</span>

                <span
                  class="c-light-black fw-500"
                  data-testid="license-breadcrumb-id"
                >
                  #{{ shortId(license.id) }}
                </span>
              </v-col>

              <v-col
                class="d-flex justify-end ms-auto"
                cols="auto"
              >
                <v-menu
                  v-model="actionsMenuOpen"
                  :close-on-content-click="true"
                  data-testid="license-actions-menu"
                  location="bottom end"
                >
                  <template #activator="{ props: activatorProps }">
                    <StandardButton
                      :bind-props="activatorProps"
                      :disabled-text="
                        adHocWorkflows.length === 0 ? 'No actions available yet' : null
                      "
                      :prevent-double-click="false"
                      append-icon="expand_more"
                      color="black"
                      custom-classes="c-black"
                      data-testid="license-actions-button"
                      size="small"
                      text="Actions"
                      variant="text"
                    />
                  </template>

                  <v-list
                    class="b-1 py-0"
                    data-testid="license-actions-list"
                    elevation="0"
                    tile
                  >
                    <template
                      v-for="(workflow, index) in adHocWorkflows"
                      :key="workflow.id"
                    >
                      <v-list-item
                        @click="onWorkflowClick(workflow)"
                        :aria-label="workflow.name"
                        :data-testid="`license-action-${workflow.id}`"
                        :title="workflow.name"
                      />

                      <v-divider
                        v-if="index + 1 < adHocWorkflows.length"
                        class="bc-black"
                      />
                    </template>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col>
                <div class="d-flex flex-column ga-2">
                  <div class="d-flex align-center ga-5">
                    <div data-testid="license-page-title">
                      <span
                        class="d-inline-block fs-24 fw-600"
                        data-testid="license-id"
                      >
                        {{ shortId(license.id) }}
                      </span>
                    </div>

                    <StandardChip
                      v-if="license.status"
                      :text="licenseStatusLabel(license.status)"
                      data-testid="license-status"
                      role="status"
                    />
                  </div>

                  <router-link
                    v-if="license.provider_id"
                    :to="resolvedProviderShowRoute"
                    class="align-self-start fs-20 c-primary underlined bc-link-hover"
                    data-testid="provider-link"
                  >
                    {{ license.provider_name }}
                  </router-link>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </PageTitle>

      <div data-testid="license-show-tabs">
        <v-tabs
          v-model="tab"
          @update:model-value="tabChange"
          bg-color="surface"
        >
          <v-tab
            v-for="tabDefinition in tabs"
            :key="tabDefinition"
            :data-testid="`license-tab-${tabDefinition}`"
            :value="tabDefinition"
            class="text-capitalize"
          >
            {{ $bct(tabDefinition) }}
          </v-tab>
        </v-tabs>
      </div>

      <v-window
        v-model="tab"
        class="border-t"
        data-testid="license-show-content"
      >
        <v-window-item value="details">
          <LicenseShowDetails
            @save="license = $event"
            :license="license"
            container-class="px-1 py-1"
          />
        </v-window-item>

        <v-window-item value="workflows">
          <div class="d-flex flex-column ga-3 ma-3">
            <LicenseWorkflowsTable />
          </div>
        </v-window-item>

        <v-window-item value="records">
          <div class="d-flex flex-column ga-3 ma-3">
            <StandardCollapseCard
              card-test-id="license-records-forms-section"
              title="Forms"
            >
              <LicenseFormsTable
                v-if="route.params.id"
                :license-id="route.params.id"
              />
            </StandardCollapseCard>

            <StandardCollapseCard
              card-test-id="license-records-public-records-section"
              title="Public records"
            >
              <LicensePublicRecordsTable
                v-if="route.params.id"
                :license-id="route.params.id"
              />
            </StandardCollapseCard>
          </div>
        </v-window-item>
      </v-window>
    </template>
  </v-container>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';

import LicenseFormsTable from '@/shared/components/licenses/LicenseFormsTable.vue';
import LicensePublicRecordsTable from '@/shared/components/licenses/LicensePublicRecordsTable.vue';
import LicenseShowDetails from '@/shared/components/licenses/LicenseShowDetails.vue';
import { licenseStatusLabel } from '@/shared/assets/constants';
import LicenseWorkflowsTable from '@/shared/components/licenses/LicenseWorkflowsTable.vue';
import PageTitle from '@/shared/components/PageTitle.vue';
import { shortId } from '@/plugins/filters';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';
import useRouterHelper from '@/shared/composables/useRouterHelper';

const { isSpecialist, isManager } = useRole();
const route = useRoute();
const router = useRouter();
const { scopedApi } = useApi();
const { updateQuery } = useRouterHelper();

const $bct = inject('translator');

const props = defineProps({
  backName: {
    type: String,
    default: 'Licenses',
  },
  backRoute: {
    type: Object,
    required: true,
  },
  providerShowRoute: {
    type: Object,
    default: undefined,
  },
});

const adHocWorkflows = ref([]);
const actionsMenuOpen = ref(false);
const license = ref(null);
const tab = ref('details');

const tabs = ['details', 'workflows', 'records'];

const LEGACY_TAB_ALIASES = { tasks: 'workflows' };

const resolvedProviderShowRoute = computed(() => {
  if (props.providerShowRoute) return props.providerShowRoute;

  if (!license.value?.provider_id) return undefined;

  return {
    name: 'ProviderShow',
    params: { providerId: license.value.provider_id },
    query: { tab: 'licensing' },
  };
});

function resolveTabFromQuery(queryTab) {
  if (!queryTab) return null;

  const resolvedTab = LEGACY_TAB_ALIASES[queryTab] ?? queryTab;
  return tabs.includes(resolvedTab) ? resolvedTab : null;
}

async function load() {
  const resolvedTab = resolveTabFromQuery(route.query?.tab);
  if (resolvedTab) await tabChange(resolvedTab);

  await loadLicense();
}

async function loadLicense() {
  const response = await scopedApi.license.get(route.params.id);
  license.value = response.data;
  await resolveAdHocWorkflows();
}

async function onWorkflowClick(workflow) {
  actionsMenuOpen.value = false;

  const response = await scopedApi.workflowInstance.create({
    workflow_template_id: workflow.workflow_template_id,
    license_id: license.value?.id,
  });

  const workflowInstanceId = response?.data?.id;
  if (!workflowInstanceId) return;

  const licenseRouteId = specialistLicenseRouteId();

  if (isSpecialist) {
    await router.push({
      name: 'LicenseWorkflowInstanceShow',
      params: {
        licenseId: licenseRouteId,
        workflowInstanceId,
      },
    });
    return;
  }

  if (isManager) {
    await router.push({
      name: 'WorkflowInstanceShow',
      params: { workflowInstanceId },
      query: { license_id: licenseRouteId },
    });
  }
}

async function resolveAdHocWorkflows() {
  if (!license.value?.licensing_program_id) return;

  const response = await scopedApi.licensingProgram.get(license.value.licensing_program_id);
  const allWorkflows = response?.data?.licensing_program_workflow_templates || [];
  adHocWorkflows.value = allWorkflows.filter((workflow) => !workflow.designation);
}

function specialistLicenseRouteId() {
  return route.params.licenseId ?? route.params.id ?? license.value?.id;
}

async function tabChange(newValue) {
  tab.value = newValue;
  await updateQuery({ tab: tab.value });
}

onMounted(load);
</script>
